<template>
  <v-toolbar
    app
    :clipped-left="left"
    :clipped-right="right"
    :style="{
      backgroundColor,
      color,
      ZIndex: `${maxZIndex + 50} !important`,
      paddingRight: `${dynamicPaddingRight}px`
    }"
    :height="appBarHeight"
    :extended="extended"
  >
    <div class="u-flex u-flexAlignItemsCenter u-flexJustifyBetween u-sizeFull">
      <slot name="keepalive"></slot>

      <div class="u-sizeFit">
        <SlcAppBarLeft
          v-show="!mobileSearchActive"
          :showBackArrow="showBackArrow"
          :showMenu="showMenu"
          :color="color"
          :title="title"
          :greyscale-logo="backgroundColor !== '#fff'"
          @backButtonClick="$emit('app-bar:back')"
          @menuButtonClick="$emit('toggleLeftNav')"
        />
      </div>

      <div class="u-sizeFill">
        <slot v-if="showSearchBar" name="search" />
      </div>

      <!-- Cancel mobile search button -->
      <slot name="closeMobileSearchButton" />
      <slc-help-drop-down
        v-if="$vuetify.breakpoint.mdAndUp"
        :dark="getTextColor({ hex: backgroundColor }) === 'white'"
      ></slc-help-drop-down>
      <dpa-id-collab-folder-menu
        v-if="$vuetify.breakpoint.mdAndUp"
        :active-folder-id="selectedFolderId"
        :show="showFolderMenu"
        class="ml-3"
        @show="showFolderMenu = true"
        @hide="showFolderMenu = false"
        @itemClick="folderClickedHandler"
        :dark="getTextColor({ hex: backgroundColor }) === 'white'"
      />
      <div v-if="!mobileSearchActive" class="u-sizeFit u-flex">
        <DpaIdAppSwichter
          class="ml-3"
          :is-light="getTextColor({ hex: backgroundColor }) === 'white'"
        />
        <DpaIdUser
          v-if="dpaSelectToken"
          class="ml-3"
          :token="dpaSelectToken"
          :profile-url="dpaIdProfileUrl"
          @logout-click="handleLogoutClick"
        />
      </div>
    </div>

    <!-- AppBar extension -->
    <slot v-if="extended" name="extension" slot="extension" />
  </v-toolbar>
</template>
<script>
import DpaIdAppSwichter from "@dpa-id-components/dpa-id-app-switcher";
import DpaIdUser from "@dpa-connect/dpa-id-user";
import { DpaIdCollabFolderMenu } from "@dpa-connect/dpa-id-collab";
import { defineComponent, computed, ref, getCurrentInstance, watch } from "vue";
import SlcAppBarLeft from "./lib/SlcAppBarLeft.vue";
import SlcHelpDropDown from "@/components/SlcHelpDropDown/SlcHelpDropDown.vue";
import { contrastingTextToBackgroundColor as getTextColor } from "@/utils/utils-color";
import maxZIndex from "@/views/lib/useMaxZIndex";
import store from "@/store";
import windowHelper from "@/store/helpers/window";
import redirect from "@/utils/redirect";

const ls = windowHelper().localStorage;

export default defineComponent({
  components: {
    DpaIdAppSwichter,
    DpaIdUser,
    SlcAppBarLeft,
    DpaIdCollabFolderMenu,
    SlcHelpDropDown
  },
  props: {
    showSearchBar: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: "#fff"
    },
    color: {
      type: String,
      default: "#000"
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    authIsLoading: {
      type: Boolean,
      default: false
    },
    /**
     * The url of the user profile at dpa-ID.
     */
    dpaIdProfileUrl: {
      type: String,
      default: ""
    },
    /**
     * The JWT token from dpa-Select useradmin.
     */
    dpaSelectToken: {
      type: String,
      default: ""
    },
    left: {
      type: Boolean,
      default: true
    },
    leftWidth: {
      type: Number,
      default: 0
    },
    right: {
      type: Boolean,
      default: false
    },
    rightWidth: {
      type: Number,
      default: 0
    },
    showBackArrow: {
      type: Boolean,
      default: false
    },
    showMenu: {
      type: Boolean,
      default: true
    },
    profile: {
      type: Object,
      default: () => ({
        firstName: "John",
        lastName: "Doe",
        sub: "john.doe@example.com"
      })
    },
    title: {
      type: String,
      default: "dpa select"
    },
    extended: {
      type: Boolean,
      default: false
    },
    avatarSize: {
      type: Number,
      default: 48
    },
    mobileSearchActive: {
      type: Boolean,
      default: false
    },
    closeMobileSearchButton: {
      type: Boolean,
      default: false
    },
    appBarHeight: {
      type: Number,
      default: 64
    },
    resizeHeader: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const root = getCurrentInstance();
    const showFolderMenu = ref(false);

    const collaboration = computed(
      () => root?.proxy.$store.state.collaboration
    );
    const selectedFolder = computed(() => collaboration.value?.selectedFolder);
    const selectedFolderId = computed(() => {
      return selectedFolder.value ? selectedFolder.value.id : null;
    });

    const dynamicPaddingRight = ref(0);
    const resizeHeaderComputed = computed(() => {
      return props.resizeHeader;
    });

    const handleResizeHeader = () => {
      if (resizeHeaderComputed.value === false) {
        dynamicPaddingRight.value = 0;
      } else {
        dynamicPaddingRight.value = 400;
      }
    };

    watch(resizeHeaderComputed, () => {
      handleResizeHeader();
    });

    function folderClickedHandler(folder) {
      dynamicPaddingRight.value = showFolderMenu.value ? 400 : 0;

      // eslint-disable-next-line no-unused-expressions
      root?.proxy.$store.commit("collaboration/setSelectedFolder", folder);
      showFolderMenu.value = false;
      // eslint-disable-next-line no-unused-expressions
      root?.proxy.$store.commit("collaboration/openSidebar", folder);
    }

    return {
      showFolderMenu,
      folderClickedHandler,
      selectedFolderId,
      getTextColor,
      maxZIndex,
      dynamicPaddingRight
    };
  },
  computed: {
    location: () => window.location,
    leftStyleObj() {
      return {
        width: `${this.leftWidth}px`,
        "max-width": `${this.leftWidth}px`
      };
    },
    rightStyleObj() {
      return {
        width: `${this.rightWidth}px`,
        "max-width": `${this.rightWidth}px`
      };
    },
    showDPALoginButton() {
      return (
        !this.isAuthenticated && !this.authIsLoading && !this.mobileSearchActive
      );
    }
  },
  methods: {
    /**
     * Emits logout event
     * @return {Void}
     */
    handleLogoutClick() {
      const sessionAuthJWT = ls.getItem("dpaID-token-select");
      if (sessionAuthJWT) {
        ls.removeItem("dpaID-token-select");
      }
      const auth0URL = store.state.stageconfig.config.ssoURLAuth0;
      if (auth0URL && !window.location.pathname.match(/\/unauthorized/)) {
        const clientId = store.state.stageconfig.config.auth0ClientId;
        const originUrl = window.location.origin;
        redirect(
          `${auth0URL}/logout?returnTo=${encodeURIComponent(
            originUrl
          )}&client_id=${clientId}`
        );
      }
    }
  }
});
</script>
<style lang="postcss" scoped>
@import "suitcss-utils-size";
@import "factorial-utils-font-weight";
@import "../../utils/utils-color/index.css";

/** @define SlcAppBar; weak */

.SlcAppBar-logo {
  height: 32px;
  width: 124px;
}

/deep/ .SlcAppBar-submit {
  width: 300px;
}

.v-toolbar {
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1) !important;
}

/deep/ .v-toolbar__content {
  position: relative;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1);
}

/deep/ .v-toolbar__extension {
  background-color: var(--color-white) !important;
}
</style>
<style lang="postcss">
@import "@dpa-id-components/dpa-id-app-switcher/lib/dpa-id-app-switcher.css";
@import "@dpa-connect/dpa-id-user/lib/dpa-id-user.css";

.DpaIdHubOverlay-overlay {
  right: -84px !important;
}

.DpaIdHubOverlay-overlay > .Overlay-triangle {
  right: calc(105px) !important;
}
</style>
